import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { Formik, ErrorMessage } from 'formik'
import * as yup from 'yup'
import _ from 'lodash'

// components
import { Button, TextInput } from 'components/shard'

// const
import { Size, Color } from 'const'
import Alert from 'components/Alert'

// hooks
import { useLoginHook } from 'hooks'
import { useHistory } from 'react-router-dom'
import WrapperLink from 'components/shard/WrapperLink'

const schema = () =>
  yup.object().shape({
    tanakaId: yup
      .string()
      .matches(/^[a-zA-Z0-9!?@._+-]*$/, { message: '半角英数で入力してください' })
      .required('項目が未入力です'),
    password: yup
      .string()
      .matches(/^[a-zA-Z0-9!?@._-]*$/, { message: '半角英数で入力してください' })
      .required('項目が未入力です'),
  })

function Login({ submitLogin, qs }) {
  const history = useHistory()
  const [values, setValues] = useState({})
  const [isRedirectErrorOpen, setIsRedirectErrorOpen] = useState(qs.redirected === 'true')
  const {
    isLoginRequesting,
    loginErrors,
    setLoginErrors,
    isLoginSuccess,
    execLogIn,
    resetStorageFunc,
    isSwitched,
    isAgreement,
  } = useLoginHook()

  useEffect(() => {
    const logIn = async () => {
      if (_.isEmpty(values)) {
        return
      }
      await execLogIn(values)
    }

    logIn()
  }, [values, execLogIn])

  useEffect(() => {
    if (isLoginSuccess) {
      /* 登録されていなければtanakaIdの変更画面へ */
      if (!isSwitched) {
        history.push('/tanakaid/switch')

        return
      }

      /* 登録が済んでいるが,規約に同意していない場合は、規約ページに遷移 */
      if (!isAgreement) {
        history.push('/signup')

        return
      }

      /* 上記が満たされれば会員ページTOPへ */
      history.push('/top')
    }
  }, [isLoginSuccess, history, isSwitched, isAgreement])

  useEffect(() => {
    if (qs.redirected === 'true') {
      resetStorageFunc()
    }
  }, [qs.redirected, resetStorageFunc])

  return (
    <Wrapper>
      {loginErrors.length > 0 && (
        <Alert type="error" message="エラー" description={loginErrors.join('\n')} onClose={() => setLoginErrors([])} />
      )}
      {isRedirectErrorOpen && (
        <Alert
          type="error"
          message="エラー"
          description="APIキーが無効であるため、ログアウトされました"
          onClose={() => setIsRedirectErrorOpen(false)}
        />
      )}
      <Title>ログイン</Title>
      <FormWrapper>
        <Formik
          initialValues={{ tanakaId: '', password: '' }}
          onSubmit={(values) => {
            setValues(values)
          }}
          validationSchema={schema}
        >
          {({ values, handleChange, handleBlur, handleSubmit, dirty, isValid }) => (
            <form
              onSubmit={(e) => {
                e.preventDefault() // useEffectでstateを更新するためにcomponentの自動更新を防ぐ
                handleSubmit(e)
              }}
            >
              <TextInput
                type="email"
                name="tanakaId"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.tanakaId}
                ariaLabel="tanakaId"
                placeholder="TANAKA-ID（メールアドレス）"
                errorMessage={<ErrorMessage name="tanakaId" />}
              />
              <TextInput
                type="password"
                name="password"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.password}
                ariaLabel="password"
                placeholder="パスワード"
                errorMessage={<ErrorMessage name="password" />}
              />
              <Message>TANAKA-IDはご来店時登録いただいたID（メールアドレス）です。</Message>
              <LinkWrapper>
                <IdLink to="/password" rel="noopener noreferrer">
                  パスワードを忘れた方
                </IdLink>
              </LinkWrapper>
              <ButtonWrapper>
                <Button type={'submit'} primary disabled={!isValid || !dirty || isLoginRequesting}>
                  ログイン
                </Button>
              </ButtonWrapper>
            </form>
          )}
        </Formik>
      </FormWrapper>
      <AboutLink href="https://www.tanaka-megane.co.jp/mytanaka/" target="_blank" rel="noopener noreferrer">
        My TANAKA会員サービスについて詳しくはこちら
      </AboutLink>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  display: grid;
  grid-template-rows: 80px 100px 400px 1fr;
`

const Title = styled.div`
  grid-row: 2;
  display: flex;
  align-items: flex-end;
  font-size: ${Size.FONT.HEADLINE.LEARGE};
  padding-bottom: 20px;
  border-bottom: 1px solid ${Color.LINE.NORMAL};
`

const FormWrapper = styled.div`
  grid-row: 3;
  padding-top: 30px;
`

const Message = styled.div`
  padding: 8px 0;
  color: ${Color.FONT.ANNOTATION};
`

const LinkWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: 30px;
`

const IdLink = styled(WrapperLink)`
  color: ${Color.FONT.ANNOTATION};
  text-decoration-line: underline;
`
const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding-top: 32px;
`

const AboutLink = styled.a`
  display: inline-block;
  padding: 40px 0 0 8px;
  color: ${Color.FONT.ANNOTATION};
  text-decoration: underline;
`

Login.propTypes = {
  submitLogin: PropTypes.func,
  qs: PropTypes.object,
}

export default Login
